import { getFunctions, httpsCallable } from "firebase/functions";

import { firebaseApp } from "./firebase";

export type SignatureDeliveryMethod = "email" | "sms";
export type SignatureRecipientRole = "authorized_person" | "purchaser" | "co_signer" | "team_member_witness" | "team_member" | "monarch_acceptance";
export type SignatureRecipientStatus = "pending" | "sent" | "viewed" | "signed" | "declined" | "voided";
export type SignaturePacketStatus = "draft" | "link_ready" | "sent_for_signature" | "viewed" | "partially_signed" | "signed" | "countersigned" | "finalizing" | "stored" | "voided" | "finalization_failed";

export type CentralSignatureHandoff = {
  documentId: string;
  formType: string;
  generatedFormVersion: number;
  id: string;
  packetId?: string;
  status: "queued" | "assigned" | "stored";
  title: string;
  updatedAt: string | null;
};

export type CentralSignatureRecipient = {
  consentVersion: string;
  deliveryMethods: SignatureDeliveryMethod[];
  deliveryStatus: "not_prepared" | "manual_delivery_required" | "manually_delivered" | "link_used" | "internal";
  email: string;
  id: string;
  name: string;
  phone: string;
  required: boolean;
  role: SignatureRecipientRole;
  sessionExpiresAt?: string | null;
  signedAt?: string | null;
  staffUserId?: string;
  status: SignatureRecipientStatus;
};

export type CentralSignaturePacket = {
  caseId: string;
  completedAt?: string | null;
  createdAt: string | null;
  events: CentralSignatureEvent[];
  forms: Array<{
    documentId: string;
    formType: string;
    generatedFormVersion: number;
    handoffId: string;
    title: string;
  }>;
  id: string;
  lovedOneName: string;
  provider?: "development_mock" | "docusign";
  providerStatus?: "development_only" | "configured" | "provider_gated";
  recipients: CentralSignatureRecipient[];
  signedDocumentIds?: string[];
  status: SignaturePacketStatus;
  title: string;
  updatedAt: string | null;
};

export type CentralSignatureEvent = {
  actorUid?: string;
  createdAt: string | null;
  id: string;
  recipientId?: string;
  type: string;
};

export type PublicSignatureSession = {
  completed: boolean;
  consentVersion: string;
  expiresAt: string;
  forms: Array<{ documentId: string; title: string }>;
  lovedOneName: string;
  packetId: string;
  packetTitle: string;
  provider: "development_mock" | "docusign";
  providerStatus: "development_only" | "configured" | "provider_gated";
  recipientName: string;
  recipientRole: SignatureRecipientRole;
  status: SignatureRecipientStatus;
};

const functions = getFunctions(firebaseApp, "us-east1");

export async function listCentralSignaturePackets(caseId: string) {
  const callable = httpsCallable<
    { caseId: string },
    {
      case: { disposition: string; id: string; lovedOneName: string; phone: string; primaryContact: string };
      handoffs: CentralSignatureHandoff[];
      packets: CentralSignaturePacket[];
    }
  >(functions, "listCentralSignaturePackets");
  return (await callable({ caseId })).data;
}

export async function createCentralSignaturePacket(input: {
  caseId: string;
  familySigner: {
    deliveryMethods: SignatureDeliveryMethod[];
    email: string;
    name: string;
    phone: string;
    role: "authorized_person" | "purchaser" | "co_signer";
  };
  handoffIds: string[];
  includeStaffWitness: boolean;
}) {
  const callable = httpsCallable<typeof input, { packet: CentralSignaturePacket }>(functions, "createCentralSignaturePacket");
  return (await callable(input)).data.packet;
}

export async function prepareCentralSignaturePacketLink(caseId: string, packetId: string, recipientId: string) {
  const callable = httpsCallable<
    { caseId: string; packetId: string; recipientId: string },
    { deliveryStatus: "manual_delivery_required"; expiresAt: string; packetId: string; provider: "development_mock" | "docusign"; providerStatus: "development_only" | "configured" | "provider_gated"; recipientId: string; signingUrl: string; status: SignaturePacketStatus }
  >(functions, "prepareCentralSignaturePacketLink");
  return (await callable({ caseId, packetId, recipientId })).data;
}

export async function confirmCentralSignaturePacketDelivery(caseId: string, packetId: string, recipientId: string) {
  const callable = httpsCallable<
    { caseId: string; packetId: string; recipientId: string },
    { packetId: string; status: SignaturePacketStatus }
  >(functions, "confirmCentralSignaturePacketDelivery");
  return (await callable({ caseId, packetId, recipientId })).data;
}

export async function remindCentralSignaturePacket(caseId: string, packetId: string, recipientId: string) {
  const callable = httpsCallable<
    { caseId: string; packetId: string; recipientId: string },
    { deliveryStatus: "manual_delivery_required"; expiresAt: string; packetId: string; provider: "development_mock" | "docusign"; providerStatus: "development_only" | "configured" | "provider_gated"; recipientId: string; signingUrl: string; status: SignaturePacketStatus }
  >(functions, "remindCentralSignaturePacket");
  return (await callable({ caseId, packetId, recipientId })).data;
}

export async function signCentralSignaturePacketRecipient(input: {
  caseId: string;
  consent: true;
  packetId: string;
  recipientId: string;
  typedName: string;
}) {
  const callable = httpsCallable<typeof input, { packetId: string; signedDocumentIds: string[]; status: "stored" }>(functions, "signCentralSignaturePacketRecipient");
  return (await callable(input)).data;
}

export async function retryCentralSignaturePacketFinalization(caseId: string, packetId: string) {
  const callable = httpsCallable<
    { caseId: string; packetId: string },
    { packetId: string; signedDocumentIds: string[]; status: "stored" }
  >(functions, "retryCentralSignaturePacketFinalization");
  return (await callable({ caseId, packetId })).data;
}

export async function voidCentralSignaturePacket(caseId: string, packetId: string) {
  const callable = httpsCallable<
    { caseId: string; packetId: string },
    { packetId: string; status: "voided" }
  >(functions, "voidCentralSignaturePacket");
  return (await callable({ caseId, packetId })).data;
}

export async function getPublicSignatureSession(token: string) {
  const callable = httpsCallable<{ token: string }, PublicSignatureSession>(functions, "getPublicSignatureSession");
  return (await callable({ token })).data;
}

export async function getPublicSignatureDocumentUrl(token: string, documentId: string) {
  const callable = httpsCallable<
    { documentId: string; token: string },
    { expiresInSeconds: number; url: string }
  >(functions, "getPublicSignatureDocumentUrl");
  return (await callable({ documentId, token })).data;
}

export async function completePublicSignatureSession(token: string, typedName: string) {
  const callable = httpsCallable<
    { consent: true; token: string; typedName: string },
    { alreadySigned: boolean; packetId: string; status: SignaturePacketStatus }
  >(functions, "completePublicSignatureSession");
  return (await callable({ consent: true, token, typedName })).data;
}

export function getSignaturePacketErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  const message = typeof error === "object" && error && "message" in error ? String(error.message) : "";
  if (/invalid or expired|Type the signer name|consent/i.test(message)) return message.replace(/^FirebaseError:\s*/i, "");
  if (code.includes("permission-denied") || code.includes("unauthenticated")) {
    return "Your account cannot complete this signature action. Sign in again or contact an administrator.";
  }
  if (code.includes("failed-precondition")) return message.replace(/^FirebaseError:\s*/i, "") || "The packet changed. Refresh and try again.";
  if (code.includes("invalid-argument")) return message.replace(/^FirebaseError:\s*/i, "") || "Check the signature details and try again.";
  if (code.includes("not-found")) return "The selected packet, signer, or case no longer exists.";
  return "The signature packet action could not be completed. Try again.";
}
